import { AsyncFeedback, LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { pageTheme,titleTheme,cardTheme } from '../../components/consoleTheme'
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Share2, Copy, RefreshCw, Check, Users, Gift, TrendingUp, ExternalLink, Info } from 'lucide-react';

function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
}

async function authFetch(url, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

const STATUS_LABEL = {
  registered: '가입 완료',
  active: '활성',
  rewarded: '보상 지급'
};

export default function ReferralPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await authFetch('/api/referral/me');
      setData(res);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('추천 코드를 재발급 하시겠습니까?\n기존 코드는 즉시 비활성화됩니다.')) return;
    setRegenerating(true);
    try {
      await authFetch('/api/referral/regenerate', { method: 'POST' });
      await load();
    } catch (e) {
      setErr(e.message);
    }
    setRegenerating(false);
  };

  if (loading) return <Wrap><LoadingState label="불러오는 중…" /></Wrap>;
  if (err && !data) return <Wrap><AsyncFeedback error={err} onRetry={load}/></Wrap>;

  return (
    <Wrap>
      <PageTitle><Share2 size={22} /> 추천 프로그램</PageTitle>

      {/* 통계 */}
      <Stats>
        <StatCard>
          <StatIcon color="var(--accent)"><Users size={20} /></StatIcon>
          <StatBody>
            <StatLabel>총 추천</StatLabel>
            <StatValue>{data.stats.total}<small>명</small></StatValue>
          </StatBody>
        </StatCard>
        <StatCard>
          <StatIcon color="#10b981"><TrendingUp size={20} /></StatIcon>
          <StatBody>
            <StatLabel>활성 사용자</StatLabel>
            <StatValue>{data.stats.active}<small>명</small></StatValue>
          </StatBody>
        </StatCard>
        <StatCard>
          <StatIcon color="#f59e0b"><Gift size={20} /></StatIcon>
          <StatBody>
            <StatLabel>적립 보상</StatLabel>
            <StatValue>{data.stats.pendingReward.toLocaleString()}<small>P</small></StatValue>
          </StatBody>
        </StatCard>
      </Stats>

      {/* 내 코드 */}
      <Card>
        <CardHeader>
          <h2>내 추천 코드</h2>
          <RegenBtn onClick={regenerate} disabled={regenerating} title="새 코드 발급 (기존 비활성)">
            <RefreshCw size={13} className={regenerating ? 'spin' : ''} />
            재발급
          </RegenBtn>
        </CardHeader>
        <CodeRow>
          <CodeDisplay>{data.code}</CodeDisplay>
          <CopyBtn onClick={() => copy(data.code, 'code')}>
            {copied === 'code' ? <><Check size={14} /> 복사됨</> : <><Copy size={14} /> 코드 복사</>}
          </CopyBtn>
        </CodeRow>
        <ShareRow>
          <ShareLabel>공유 링크</ShareLabel>
          <ShareLink>{data.shareUrl}</ShareLink>
          <CopyBtn onClick={() => copy(data.shareUrl, 'url')}>
            {copied === 'url' ? <><Check size={14} /> 복사됨</> : <><Copy size={14} /> 링크 복사</>}
          </CopyBtn>
          <OpenBtn href={data.shareUrl} target="_blank" rel="noopener noreferrer" title="새 창에서 열기">
            <ExternalLink size={14} />
          </OpenBtn>
        </ShareRow>
        <Hint>
          이 코드를 친구에게 공유하면, 친구가 가입 시 추천 관계가 자동 등록됩니다.
          코드는 8자리 영문 대문자/숫자 조합이며 헷갈리는 0/O/1/I 는 제외됩니다.
        </Hint>
      </Card>

      {/* 보상 정책 안내 */}
      <NoticeCard>
        <Info size={18} />
        <div>
          <strong>보상 정책 안내</strong>
          <p>구체적인 적립/지급 정책은 준비 중입니다. 현재 추천 관계는 정상적으로 기록되며,
            정책이 확정되는 시점부터 소급 적용될 예정입니다.</p>
        </div>
      </NoticeCard>

      {/* 추천한 사용자 목록 */}
      <Card>
        <CardHeader><h2>추천한 사용자 ({data.referrals.length})</h2></CardHeader>
        {data.referrals.length === 0 ? (
          <Empty>
            <Users size={32} />
            <div>아직 추천한 사용자가 없습니다.</div>
            <small>위 추천 코드 또는 공유 링크를 친구에게 공유해 보세요.</small>
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>사용자</th>
                <th>사용 코드</th>
                <th>상태</th>
                <th>가입 일시</th>
                <th>활성 일시</th>
              </tr>
            </thead>
            <tbody>
              {data.referrals.map(r => (
                <tr key={r.referral_id}>
                  <td><Mono>{r.referred_id.slice(0, 8)}...</Mono></td>
                  <td><Mono>{r.code_used}</Mono></td>
                  <td><StatusBadge status={r.status}>{STATUS_LABEL[r.status] || r.status}</StatusBadge></td>
                  <td>{new Date(r.referred_at).toLocaleString('ko-KR')}</td>
                  <td>{r.activated_at ? new Date(r.activated_at).toLocaleString('ko-KR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Wrap>
  );
}

const Wrap = styled.div`
 ${pageTheme}

  padding: 28px 36px 60px;
  max-width: 960px;
  margin: 0 auto;
  text-align: left;
  @media(max-width:768px){padding:1rem 1rem 2rem;}
`;
const PageTitle = styled.h1`
 ${titleTheme}
 &&{margin-bottom:28px;}

  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 20px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;
const StatCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 14px;
`;
const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ color }) => color}15;
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;
const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  small { font-size: 12px; font-weight: 500; color: #9ca3af; margin-left: 4px; }
`;
const Card = styled.div`
 ${cardTheme}

  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
  padding: 22px;
`;
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  h2 { margin: 0; font-size: 0.95rem; font-weight: 600; color: #374151; }
`;
const RegenBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  color: #4b5563;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  .spin { animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;
const CodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border-radius: 8px;
  margin-bottom: 12px;
`;
const CodeDisplay = styled.div`
  flex: 1;
  font-family: 'Cascadia Mono', Consolas, monospace;
  font-size: 28px;
  font-weight: 700;
  color: #1e40af;
  letter-spacing: 4px;
  text-align: center;
`;
const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  background: white;
  border: 1px solid #93c5fd;
  border-radius: 6px;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #eff6ff; }
`;
const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`;
const ShareLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;
const ShareLink = styled.div`
  flex: 1;
  font-family: 'Cascadia Mono', Consolas, monospace;
  font-size: 12px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const OpenBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  color: #4b5563;
  &:hover { background: #f3f4f6; }
`;
const Hint = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
`;
const NoticeCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 14px 18px;
  margin-bottom: 16px;
  color: #92400e;
  strong { display: block; font-size: 13px; margin-bottom: 4px; }
  p { margin: 0; font-size: 12px; line-height: 1.5; color: #78350f; }
`;
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 20px;
  color: #9ca3af;
  text-align: center;
  small { color: #9ca3af; }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }
  th {
    background: #fafbfc;
    font-weight: 600;
    color: #6b7280;
    font-size: 12px;
  }
`;
const Mono = styled.span`
  font-family: 'Cascadia Mono', Consolas, monospace;
  font-size: 12px;
`;
const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'rewarded' ? '#fef3c7' :
    status === 'active' ? '#d1fae5' :
    '#dbeafe'};
  color: ${({ status }) =>
    status === 'rewarded' ? '#92400e' :
    status === 'active' ? '#065f46' :
    '#1e40af'};
`;
